import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import styles from './SupervisorApprovalPage.module.css';
import {
  SupervisorOrderListItem,
  fetchSupervisorOrders,
} from './services/supervisorApprovalAPI';

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

const DEFAULT_PAGINATION: PaginationState = {
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
};

const SupervisorApprovalPage: React.FC = () => {
  const [orders, setOrders] = useState<SupervisorOrderListItem[]>([]);
  const [loadingList, setLoadingList] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodo, setPeriodo] = useState('mensal');
  const [dataI, setDataI] = useState('');
  const [dataF, setDataF] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (periodo === 'custom') return;

    const now = new Date();
    let start = new Date();
    if (periodo === 'semanal') start.setDate(now.getDate() - 7);
    else if (periodo === 'mensal') start.setDate(now.getDate() - 30);

    setDataI(start.toISOString().slice(0, 10));
    setDataF(now.toISOString().slice(0, 10));
  }, [periodo]);

  const fetchOrders = async (page: number, filtersOverride?: { search?: string; dataI?: string; dataF?: string; status?: string }) => {
    setLoadingList(true);
    setError(null);
    try {
      const s = filtersOverride?.search ?? search;
      const di = filtersOverride?.dataI ?? dataI;
      const df = filtersOverride?.dataF ?? dataF;
      const st = filtersOverride?.status ?? statusFilter;

      const filters: any = {};
      if (s.trim()) filters.search = s.trim();
      if (di) filters.dataI = di;
      if (df) filters.dataF = df;
      if (st !== 'all') filters.status = st;

      const response = await fetchSupervisorOrders(page, 20, filters);
      setOrders(response.data.items);
      setPagination(response.data.pagination);
    } catch (e: any) {
      console.error(e);
      setError('Erro ao carregar pedidos.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!dataI || !dataF) return; // aguarda datas serem calculadas
    fetchOrders(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, dataI, dataF]);

  const handleFilter = () => {
    setCurrentPage(1);
    fetchOrders(1, { search, dataI, dataF, status: statusFilter });
  };

  const handleRowClick = (id: number) => {
    navigate(`/aprovacao-pedidos/${id}`);
  };

  const getStatusBadge = (pedido_situacao: number | null) => {
    switch (pedido_situacao) {
      case 0: return { label: 'Aguardando Supervisor', color: '#fef3c7', textColor: '#b45309' };
      case 1: return { label: 'Aguardando Amostras', color: '#eff6ff', textColor: '#2563eb' };
      case 2: return { label: 'Recusado', color: '#fee2e2', textColor: '#b91c1c' };
      case 3: return { label: 'Finalizado', color: '#dcfce3', textColor: '#15803d' };
      default: return { label: 'Pendente', color: '#f1f5f9', textColor: '#64748b' };
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <h1>Aprovação de Pedidos Externos</h1>
      </div>

      <section className={styles.registrySection}>
        <div className={styles.sectionHeader}>
          <div className={styles.filters}>
            <div className={styles.leftFilters}>
              <div className={styles.searchGroup}>
                <input
                  type="text"
                  placeholder="Buscar por ID, Cliente, CNPJ..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <div className={styles.filterItem}>
                <label>Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">Todos</option>
                  <option value="Pendente">Aguardando Supervisor</option>
                  <option value="Aprovado">Aprovado / Finalizado</option>
                  <option value="Recusado">Recusado</option>
                </select>
              </div>
            </div>

            <div className={styles.rightFilters}>
              <div className={styles.periodButtons}>
                {['semanal', 'mensal', 'custom'].map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriodo(p)}
                    className={`${styles.periodBtn} ${periodo === p ? styles.periodBtnActive : ''}`}
                  >
                    {p === 'custom' ? 'Personalizado' : p === 'semanal' ? '7 dias' : '30 dias'}
                  </button>
                ))}
              </div>

              {periodo === 'custom' && (
                <div className={styles.dateRangeContainer}>
                  <input
                    type="date"
                    value={dataI}
                    onChange={(e) => setDataI(e.target.value)}
                    className={styles.dateInput}
                  />
                  <span style={{ color: '#94a3b8' }}>até</span>
                  <input
                    type="date"
                    value={dataF}
                    onChange={(e) => setDataF(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
              )}

              <button className={styles.actionButton} onClick={handleFilter}>
                Filtrar
              </button>
            </div>
          </div>
        </div>

        {error && <p className={styles.feedbackError}>{error}</p>}

        <div style={{ overflowX: 'auto' }}>
          <table className={styles.tableContainer}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Cliente</th>
                <th>CNPJ</th>
                <th>Operação</th>
                <th>Total</th>
                <th>Situação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loadingList ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td><Skeleton width={40} /></td>
                    <td><Skeleton width={180} /></td>
                    <td><Skeleton width={120} /></td>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={80} /></td>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={80} /></td>
                  </tr>
                ))
              ) : orders.length > 0 ? (
                orders.map((order) => {
                  const badge = getStatusBadge(order.pedido_situacao);
                  return (
                    <tr key={order.id} className={styles.tableRow} onClick={() => handleRowClick(order.id)}>
                      <td>#{order.id}</td>
                      <td><strong>{order.cliente_razao_social || 'Cliente não informado'}</strong></td>
                      <td>{order.cliente_cnpj || '--'}</td>
                      <td>{order.operacao_fiscal_desc || order.operacoes_fiscais || '--'}</td>
                      <td>R$ {(Number(order.total) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <span
                          className={styles.statusBadge}
                          style={{ backgroundColor: badge.color, color: badge.textColor }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td>
                        <button className={styles.actionButton} onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(order.id);
                        }}>
                          Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className={styles.feedback}>Nenhum pedido encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(pagination?.totalPages ?? 1) > 1 && (
          <div className={styles.paginationRow}>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>
              Página {pagination?.currentPage ?? 1} de {pagination?.totalPages ?? 1}
            </span>
            <button
              type="button"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, pagination?.totalPages ?? 1))
              }
              disabled={currentPage === (pagination?.totalPages ?? 1)}
            >
              Próxima
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default SupervisorApprovalPage;
